import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useEffect,
} from "react";
import { CustomCellEditorProps } from "ag-grid-react";
import { Popover, Box, IconButton, Stack } from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import {
  Button,
  paletteVarAlpha,
  Typography,
  TextField,
} from "@platform/design-system";

import { BRAND_PURPLE } from "./editorColors";

import type { PlanEditorRowData } from "../../types/values.types";

export type BenefitDescriptionOptionType = "dollarAmount" | "salaryMultiplier";

export interface BenefitDescriptionOption {
  type: BenefitDescriptionOptionType;
  value: number | null;
}

export interface BenefitDescriptionMultiOptionsEditorParams {
  maxEntries?: number;
  title?: string;
}

export interface BenefitDescriptionMultiOptionsEditorRef {
  getValue: () => BenefitDescriptionOption[];
}

type BenefitDescriptionMultiOptionsEditorProps = CustomCellEditorProps<
  PlanEditorRowData,
  unknown
> &
  BenefitDescriptionMultiOptionsEditorParams & {
    cellEditorParams?: BenefitDescriptionMultiOptionsEditorParams;
  };

/**
 * Type guard for a stored benefit description option. Matches the shape the
 * editor persists, allowing legacy or malformed entries to be filtered out.
 */
const isBenefitDescriptionOption = (
  item: unknown,
): item is BenefitDescriptionOption =>
  item !== null &&
  typeof item === "object" &&
  "type" in item &&
  "value" in item &&
  (item.type === "dollarAmount" || item.type === "salaryMultiplier") &&
  typeof item.value === "number";

/**
 * Get placeholder text based on type
 */
const getPlaceholder = (type: BenefitDescriptionOptionType): string => {
  if (type === "dollarAmount") {
    return "$X";
  }
  return "X times salary";
};

/**
 * Benefit Description Multi-Options Editor
 *
 * Allows users to add multiple benefit options of two types:
 * - Dollar amounts (e.g., $5,000, $10,000)
 * - Salary multipliers (e.g., 2x salary, 3x salary)
 *
 * Maximum 10 entries total across both types.
 */
export const BenefitDescriptionMultiOptionsEditor = forwardRef<
  BenefitDescriptionMultiOptionsEditorRef,
  BenefitDescriptionMultiOptionsEditorProps
>((props, ref) => {
  const { stopEditing, api, node, column } = props;
  const value = props.value;

  // AG Grid passes cellEditorParams through the params object
  const editorParams: BenefitDescriptionMultiOptionsEditorParams =
    props.cellEditorParams ?? props;
  const maxEntries = editorParams.maxEntries ?? 10;

  // Initialize entries from value or create empty array
  // Handle case where value might be a number (from previous dollarAmount type)
  let initialEntries: BenefitDescriptionOption[] = [];
  if (Array.isArray(value)) {
    initialEntries = value.filter(isBenefitDescriptionOption);
  }

  // If starting with no entries, pre-populate with one of each type
  if (initialEntries.length === 0) {
    initialEntries = [
      { type: "dollarAmount", value: null },
      { type: "salaryMultiplier", value: null },
    ];
  }

  const [entries, setEntries] =
    useState<BenefitDescriptionOption[]>(initialEntries);

  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  // Expose getValue method to AG Grid
  useImperativeHandle(ref, () => ({
    getValue: () => entries,
  }));

  // Auto-open popover when editor is created
  useEffect(() => {
    const timer = setTimeout(() => {
      setOpen(true);
    }, 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = (): void => {
    setOpen(false);
    // Stop editing and apply the value when popover closes
    if (stopEditing) {
      setTimeout(() => {
        stopEditing();
      }, 0);
    }
  };

  const handleDone = (): void => {
    // Set the data value directly on the node using the column field name
    if (node && column) {
      const columnField = column.getColId();
      node.setDataValue(columnField, entries);
    }

    // Force AG Grid to accept the current value and refresh the cell
    if (stopEditing) {
      stopEditing();
    }

    // Refresh the cell to update the display
    if (api && node) {
      setTimeout(() => {
        api.refreshCells({ rowNodes: [node], force: true });
      }, 0);
    }

    setOpen(false);
  };

  const handleAddEntry = (type: BenefitDescriptionOptionType): void => {
    if (entries.length >= maxEntries) {
      return;
    }

    const newEntry: BenefitDescriptionOption = {
      type,
      value: null,
    };

    setEntries([...entries, newEntry]);
  };

  const handleRemoveEntry = (index: number): void => {
    const entryToRemove = entries[index];
    const remainingOfSameType = entries.filter(
      (entry) => entry.type === entryToRemove.type,
    ).length;

    // Prevent removing the last entry of each type
    if (remainingOfSameType <= 1) {
      return;
    }

    setEntries(entries.filter((_, i) => i !== index));
  };

  const handleValueChange = (index: number, newValue: string): void => {
    // Allow empty string (null value)
    if (newValue === "") {
      const updatedEntries = [...entries];
      updatedEntries[index] = {
        ...updatedEntries[index],
        value: null,
      };
      setEntries(updatedEntries);
      return;
    }

    const numValue = parseFloat(newValue);

    // Reject invalid or negative values
    if (isNaN(numValue) || numValue < 0) {
      return;
    }

    const updatedEntries = [...entries];
    updatedEntries[index] = {
      ...updatedEntries[index],
      value: numValue,
    };
    setEntries(updatedEntries);
  };

  // Check if all entries have valid values (not null/undefined and > 0)
  const hasInvalidEntries = entries.some(
    (entry) => entry.value == null || entry.value <= 0,
  );

  // Group entries by type for display
  const dollarEntries = entries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => entry.type === "dollarAmount");

  const salaryEntries = entries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => entry.type === "salaryMultiplier");

  const renderEntry = (
    entry: BenefitDescriptionOption,
    index: number,
  ): React.JSX.Element => {
    const inputProps =
      entry.type === "dollarAmount"
        ? {
            type: "number" as const,
            inputProps: { min: 0, step: 1000 },
          }
        : {
            type: "number" as const,
            inputProps: { min: 0, step: 0.5, max: 10 },
          };

    // Check if this is the last entry of its type
    const remainingOfSameType = entries.filter(
      (e) => e.type === entry.type,
    ).length;
    const isLastOfType = remainingOfSameType <= 1;

    return (
      <Box
        key={index}
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
        }}
      >
        <TextField
          value={entry.value || ""}
          onChange={(e) => {
            e.stopPropagation();
            handleValueChange(index, e.target.value);
          }}
          onFocus={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          size="sm"
          fullWidth
          placeholder={getPlaceholder(entry.type)}
          autoComplete="off"
          {...inputProps}
        />
        {!isLastOfType && (
          <IconButton size="small" onClick={() => handleRemoveEntry(index)}>
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  };

  const hasReachedMax = entries.length >= maxEntries;

  return (
    <>
      <div ref={anchorRef} style={{ width: "100%", height: "100%" }} />
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableEnforceFocus
        disableAutoFocus={false}
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              minWidth: 400,
              maxWidth: 600,
              maxHeight: 600,
              overflow: "auto",
            },
          },
        }}
      >
        <Box sx={{ p: 2 }} onClick={(e) => e.stopPropagation()}>
          <Stack spacing={2} sx={{ mb: 2 }}>
            {/* Dollar Amount Entries */}
            {dollarEntries.length > 0 && (
              <Stack spacing={1}>
                {dollarEntries.map(({ entry, index }) =>
                  renderEntry(entry, index),
                )}
              </Stack>
            )}

            {/* Add Dollar Amount Button */}
            <Box
              onClick={() => !hasReachedMax && handleAddEntry("dollarAmount")}
              sx={{
                border: `1px dashed ${paletteVarAlpha(BRAND_PURPLE, 0.5)}`,
                borderRadius: "4px",
                color: BRAND_PURPLE,
                bgcolor: "transparent",
                textTransform: "uppercase",
                fontSize: "0.8125rem",
                fontWeight: 500,
                padding: "9px 10px",
                textAlign: "center",
                cursor: hasReachedMax ? "not-allowed" : "pointer",
                transition: "border-color 0.2s ease-in-out",
                userSelect: "none",
                "&:hover": hasReachedMax
                  ? {}
                  : {
                      borderColor: paletteVarAlpha(BRAND_PURPLE, 0.8),
                    },
                ...(hasReachedMax && {
                  borderColor: paletteVarAlpha(BRAND_PURPLE, 0.3),
                  color: paletteVarAlpha(BRAND_PURPLE, 0.5),
                }),
              }}
            >
              Add Option - Dollar
            </Box>

            {/* Salary Multiplier Entries */}
            {salaryEntries.length > 0 && (
              <Stack spacing={1}>
                {salaryEntries.map(({ entry, index }) =>
                  renderEntry(entry, index),
                )}
              </Stack>
            )}

            {/* Add Salary Multiplier Button */}
            <Box
              onClick={() =>
                !hasReachedMax && handleAddEntry("salaryMultiplier")
              }
              sx={{
                border: `1px dashed ${paletteVarAlpha(BRAND_PURPLE, 0.5)}`,
                borderRadius: "4px",
                color: BRAND_PURPLE,
                bgcolor: "transparent",
                textTransform: "uppercase",
                fontSize: "0.8125rem",
                fontWeight: 500,
                padding: "9px 10px",
                textAlign: "center",
                cursor: hasReachedMax ? "not-allowed" : "pointer",
                transition: "border-color 0.2s ease-in-out",
                userSelect: "none",
                "&:hover": hasReachedMax
                  ? {}
                  : {
                      borderColor: paletteVarAlpha(BRAND_PURPLE, 0.8),
                    },
                ...(hasReachedMax && {
                  borderColor: paletteVarAlpha(BRAND_PURPLE, 0.3),
                  color: paletteVarAlpha(BRAND_PURPLE, 0.5),
                }),
              }}
            >
              Add Option - Salary Multiplier
            </Box>
          </Stack>

          {hasReachedMax && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ display: "block", mb: 2 }}
            >
              Maximum of {maxEntries} options reached
            </Typography>
          )}

          {hasInvalidEntries && (
            <Typography
              variant="caption"
              color="danger"
              sx={{ display: "block", mb: 2 }}
            >
              All options must have a value greater than 0
            </Typography>
          )}

          <Box
            sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}
          >
            <Button
              onClick={handleDone}
              variant="primary"
              disabled={hasInvalidEntries}
            >
              Done
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
});

BenefitDescriptionMultiOptionsEditor.displayName =
  "BenefitDescriptionMultiOptionsEditor";
